import type { BlacklistedDomain, BlacklistedProxy } from '@/api/types'
import { stripWww } from '@/lib/format'

/** Identifies one POS in the blacklist data: by its name, or by its domain when the proxy API does not know the POS. */
export interface PosRef {
  name: string | null
  domain: string
}

export interface PosBlacklist {
  key: string
  label: string
  /** Blacklisted IPs across every domain of the POS. */
  count: number
  domains: string[]
  pos: PosRef
}

/** The POS a list is narrowed to, with the name to show in its title. */
export interface PosScope {
  label: string
  pos: PosRef
}

/** One entry per POS, adding up the IPs of all its domains, most blacklisted first. */
export function groupByPos(domains: BlacklistedDomain[]): PosBlacklist[] {
  const groups = new Map<string, PosBlacklist>()
  for (const d of domains) {
    if (d.blacklistedIpCount <= 0) continue
    const host = stripWww(d.domainName)
    const name = d.channelName || null
    const key = name ? `name:${name}` : `domain:${host}`
    const group = groups.get(key) ?? { key, label: name ?? host, count: 0, domains: [], pos: { name, domain: host } }
    group.count += d.blacklistedIpCount
    if (!group.domains.includes(host)) group.domains.push(host)
    groups.set(key, group)
  }
  return [...groups.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

/** Whether a blacklisted proxy belongs to the POS: by POS name, or by domain for a POS the proxy API does not name. */
export function matchesPos(proxy: BlacklistedProxy, pos: PosRef): boolean {
  return pos.name ? proxy.channelName === pos.name : stripWww(proxy.domain) === pos.domain
}
